/**
 * @fileoverview Modal que conduz o pareamento por PASSKEY (WebAuthn) de uma
 * conexão WAHA. O WhatsApp exige passkey ao vincular alguns números; a sessão
 * pausa em PASSKEY_REQUIRED. A assinatura só ocorre num navegador na origem
 * web.whatsapp.com — via extensão do WAHA (caminho principal) ou via script no
 * DevTools (fallback). O painel apenas orquestra: busca o desafio no nosso
 * backend (que fala com o WAHA usando a API Key — nunca exposta ao browser),
 * manda assinar, e devolve a assertion.
 */

import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  CircularProgress,
  Collapse,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Check, Copy, ExternalLink, RefreshCw } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import {
  PASSKEY_FIREFOX_URL,
  passkeyChromeStoreUrl,
  isFirefox,
  pingPasskeyExtension,
  signPasskeyChallenge,
  buildDevtoolsScript,
} from "../../helpers/passkeyExtension";

const PRIMARY = "#3D5CFD";
const PRIMARY_DARK = "#2B46D4";
const PRIMARY_SOFT = "#EEF2FF";
const ACCENT = "#7C3AED";
const INK = "#0F172A";
const INK_MUTED = "#475569";
const LINE = "#E2E8F0";
const SUCCESS = "#10B981";

const useStyles = makeStyles({
  paper: {
    borderRadius: 20,
    width: 520,
    maxWidth: "calc(100% - 32px)",
    background: "#ffffff",
    overflow: "hidden",
  },
  header: {
    padding: "28px 32px 8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 8,
  },
  badge: {
    width: 52,
    height: 52,
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    fontSize: 26,
    background: `linear-gradient(135deg, ${PRIMARY_SOFT}, #F5F3FF)`,
    border: `1px solid rgba(124, 58, 237, 0.18)`,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: INK,
    letterSpacing: "-0.01em",
  },
  subtitle: {
    fontSize: 13.5,
    color: INK_MUTED,
    lineHeight: 1.5,
    maxWidth: 400,
  },
  content: { padding: "12px 32px 8px" },
  section: { display: "flex", flexDirection: "column", gap: 14 },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: INK },
  sectionText: { fontSize: 13.5, color: INK_MUTED, lineHeight: 1.55 },
  inlineWait: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 13.5,
    color: INK_MUTED,
  },
  extOk: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 600,
    color: SUCCESS,
    background: "#ECFDF5",
    border: "1px solid rgba(16,185,129,0.28)",
    padding: "8px 12px",
    borderRadius: 10,
    alignSelf: "flex-start",
  },
  primaryBtn: {
    textTransform: "none",
    background: PRIMARY,
    color: "#fff",
    fontWeight: 600,
    fontSize: 14,
    padding: "11px 18px",
    borderRadius: 12,
    boxShadow: "0 10px 22px -8px rgba(61,92,253,0.55)",
    "&:hover": { background: PRIMARY_DARK },
    "&.Mui-disabled": { background: "#CBD5E1", color: "#fff" },
  },
  hint: {
    fontSize: 12.5,
    color: ACCENT,
    background: "#F5F3FF",
    border: "1px solid rgba(124,58,237,0.18)",
    borderRadius: 10,
    padding: "10px 12px",
    lineHeight: 1.5,
  },
  installBox: { display: "flex", flexDirection: "column", gap: 12 },
  installBtns: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" },
  installBtn: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    color: PRIMARY,
    borderColor: "rgba(61,92,253,0.35)",
    borderRadius: 10,
    padding: "8px 14px",
  },
  ghostBtn: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    color: INK_MUTED,
    borderRadius: 10,
    padding: "8px 12px",
  },
  fallbackLink: {
    alignSelf: "flex-start",
    background: "none",
    border: "none",
    padding: "4px 0",
    marginTop: 2,
    color: INK_MUTED,
    fontSize: 12.5,
    fontWeight: 600,
    textDecoration: "underline",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  fallbackBox: {
    marginTop: 10,
    padding: 16,
    borderRadius: 14,
    background: "#F8FAFC",
    border: `1px solid ${LINE}`,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  stepList: {
    margin: 0,
    paddingLeft: 18,
    fontSize: 13,
    color: "#334155",
    lineHeight: 1.6,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  scriptRow: { display: "flex", flexDirection: "column", gap: 8 },
  scriptArea: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily:
      '"JetBrains Mono","Fira Code",ui-monospace,Menlo,Consolas,monospace',
    fontSize: 11.5,
    lineHeight: 1.5,
    color: INK,
    background: "#0F172A0A",
    border: `1px solid ${LINE}`,
    borderRadius: 10,
    padding: "10px 12px",
    resize: "vertical",
  },
  copyBtn: {
    alignSelf: "flex-start",
    textTransform: "none",
    fontSize: 12.5,
    fontWeight: 600,
    color: PRIMARY,
    border: `1px solid rgba(61,92,253,0.28)`,
    borderRadius: 999,
    padding: "5px 14px",
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: INK_MUTED,
  },
  pasteArea: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily:
      '"JetBrains Mono","Fira Code",ui-monospace,Menlo,Consolas,monospace',
    fontSize: 11.5,
    lineHeight: 1.5,
    color: INK,
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 10,
    padding: "10px 12px",
    resize: "vertical",
  },
  codeCard: {
    alignSelf: "stretch",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    padding: "18px 16px",
    borderRadius: 14,
    background: `linear-gradient(180deg,#fff, ${PRIMARY_SOFT})`,
    border: `1px solid rgba(61,92,253,0.18)`,
  },
  codeLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    color: PRIMARY,
  },
  codeValue: {
    fontFamily:
      '"JetBrains Mono","Fira Code",ui-monospace,Menlo,Consolas,monospace',
    fontSize: 30,
    fontWeight: 700,
    letterSpacing: "0.2em",
    color: INK,
  },
  doneBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    padding: "18px 0 8px",
    textAlign: "center",
  },
  doneIcon: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: "#ECFDF5",
    color: SUCCESS,
    display: "grid",
    placeItems: "center",
    border: "1px solid rgba(16,185,129,0.3)",
  },
  doneTitle: { fontSize: 18, fontWeight: 700, color: INK },
  doneSubtitle: { fontSize: 13.5, color: INK_MUTED },
  errorBox: {
    marginTop: 14,
    fontSize: 13,
    color: "#B91C1C",
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 10,
    padding: "10px 12px",
    lineHeight: 1.5,
    wordBreak: "break-word",
  },
  actions: { padding: "10px 24px 20px", justifyContent: "flex-end" },
  closeBtn: {
    textTransform: "none",
    color: INK_MUTED,
    fontWeight: 600,
    fontSize: 13,
    borderRadius: 10,
    "&:hover": { background: "#F1F5F9" },
  },
});

const PasskeyModal = ({ open, onClose, whatsAppId, initialStatus }) => {
  const classes = useStyles();
  const socketManager = useContext(SocketContext);
  const firefox = isFirefox();

  const [extState, setExtState] = useState("checking"); // checking | present | absent
  const [phase, setPhase] = useState("idle"); // idle | signing | submitting | confirming | done | error
  const [error, setError] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [needConfirm, setNeedConfirm] = useState(false);
  const [devtoolsOpen, setDevtoolsOpen] = useState(false);
  const [devtoolsScript, setDevtoolsScript] = useState("");
  const [pastedAssertion, setPastedAssertion] = useState("");
  const [scriptCopied, setScriptCopied] = useState(false);
  const closedRef = useRef(false);

  const fetchConfirmation = useCallback(async () => {
    if (!whatsAppId) return;
    try {
      const { data } = await api.get(
        `/whatsapp/${whatsAppId}/passkey/confirmation`
      );
      setConfirmationCode((data && data.code) || "");
    } catch (e) {
      // O código pode ainda não estar disponível — silencioso.
    }
  }, [whatsAppId]);

  const recheckExtension = useCallback(() => {
    setExtState("checking");
    pingPasskeyExtension().then((ok) =>
      setExtState(ok ? "present" : "absent")
    );
  }, []);

  // Detecta a extensão ao abrir.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setExtState("checking");
    pingPasskeyExtension().then((ok) => {
      if (active) setExtState(ok ? "present" : "absent");
    });
    return () => {
      active = false;
    };
  }, [open]);

  // Estado inicial de confirmação + reset ao fechar.
  useEffect(() => {
    if (open) {
      const confirm =
        String(initialStatus || "").toUpperCase() ===
        "PASSKEY_CONFIRMATION_REQUIRED";
      setNeedConfirm(confirm);
      if (confirm) fetchConfirmation();
    } else {
      setPhase("idle");
      setError("");
      setConfirmationCode("");
      setNeedConfirm(false);
      setDevtoolsOpen(false);
      setDevtoolsScript("");
      setPastedAssertion("");
      setScriptCopied(false);
      closedRef.current = false;
    }
  }, [open, initialStatus, fetchConfirmation]);

  // Acompanha o status da sessão em tempo real.
  useEffect(() => {
    if (!whatsAppId || !open) return undefined;
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const handler = (data) => {
      if (!data || data.action !== "update" || data.session.id !== whatsAppId)
        return;
      const st = String(data.session.status || "").toUpperCase();
      if (st === "CONNECTED" || st === "WORKING") {
        setPhase("done");
        if (!closedRef.current) {
          closedRef.current = true;
          setTimeout(() => onClose(), 1600);
        }
      } else if (st === "PASSKEY_CONFIRMATION_REQUIRED") {
        setNeedConfirm(true);
        setPhase("idle");
        fetchConfirmation();
      } else if (st === "FAILED") {
        setPhase("error");
        setError(i18n.t("passkeyModal.errors.failed"));
      }
    };

    socket.on(`company-${companyId}-whatsappSession`, handler);
    return () =>
      socket.off(`company-${companyId}-whatsappSession`, handler);
  }, [whatsAppId, open, socketManager, onClose, fetchConfirmation]);

  const runExtensionSign = async () => {
    setError("");
    setPhase("signing");
    try {
      const { data: challenge } = await api.get(
        `/whatsapp/${whatsAppId}/passkey/challenge`
      );
      const assertion = await signPasskeyChallenge(challenge);
      setPhase("submitting");
      await api.post(`/whatsapp/${whatsAppId}/passkey`, assertion);
      // Aguarda o webhook (CONNECTED ou PASSKEY_CONFIRMATION_REQUIRED).
      setPhase("idle");
    } catch (e) {
      setPhase("error");
      setError(
        (e && e.response && e.response.data && e.response.data.error) ||
          (e && e.message) ||
          i18n.t("passkeyModal.errors.sign")
      );
    }
  };

  const openDevtoolsFallback = async () => {
    setError("");
    try {
      const { data: challenge } = await api.get(
        `/whatsapp/${whatsAppId}/passkey/challenge`
      );
      setDevtoolsScript(buildDevtoolsScript(challenge));
      setDevtoolsOpen(true);
    } catch (e) {
      toastError(e);
    }
  };

  const submitPastedAssertion = async () => {
    setError("");
    let assertion;
    try {
      assertion = JSON.parse(pastedAssertion);
    } catch (e) {
      setPhase("error");
      setError(i18n.t("passkeyModal.errors.assertionParse"));
      return;
    }
    setPhase("submitting");
    try {
      await api.post(`/whatsapp/${whatsAppId}/passkey`, assertion);
      setPhase("idle");
    } catch (e) {
      setPhase("error");
      setError(
        (e && e.response && e.response.data && e.response.data.error) ||
          i18n.t("passkeyModal.errors.sign")
      );
    }
  };

  const confirmPairing = async () => {
    setError("");
    setPhase("confirming");
    try {
      await api.post(`/whatsapp/${whatsAppId}/passkey/confirm`);
      setPhase("idle");
    } catch (e) {
      setPhase("error");
      setError(
        (e && e.response && e.response.data && e.response.data.error) ||
          i18n.t("passkeyModal.errors.confirm")
      );
    }
  };

  const copyScript = async () => {
    try {
      await navigator.clipboard.writeText(devtoolsScript);
      setScriptCopied(true);
      setTimeout(() => setScriptCopied(false), 2000);
    } catch (e) {
      /* noop */
    }
  };

  const busy = phase === "signing" || phase === "submitting";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{ className: classes.paper }}
    >
      <div className={classes.header}>
        <div className={classes.badge}>🔐</div>
        <Typography className={classes.title}>
          {i18n.t("passkeyModal.title")}
        </Typography>
        <Typography className={classes.subtitle}>
          {i18n.t("passkeyModal.subtitle")}
        </Typography>
      </div>

      <DialogContent className={classes.content}>
        {phase === "done" ? (
          <div className={classes.doneBox}>
            <div className={classes.doneIcon}>
              <Check size={28} />
            </div>
            <Typography className={classes.doneTitle}>
              {i18n.t("passkeyModal.doneTitle")}
            </Typography>
            <Typography className={classes.doneSubtitle}>
              {i18n.t("passkeyModal.doneSubtitle")}
            </Typography>
          </div>
        ) : needConfirm ? (
          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {i18n.t("passkeyModal.confirmTitle")}
            </Typography>
            <Typography className={classes.sectionText}>
              {i18n.t("passkeyModal.confirmSubtitle")}
            </Typography>
            {confirmationCode ? (
              <div className={classes.codeCard}>
                <span className={classes.codeLabel}>
                  {i18n.t("passkeyModal.confirmCodeLabel")}
                </span>
                <span className={classes.codeValue}>{confirmationCode}</span>
              </div>
            ) : (
              <div className={classes.inlineWait}>
                <CircularProgress size={16} />
                <span>{i18n.t("passkeyModal.confirmLoadingCode")}</span>
              </div>
            )}
            <Button
              className={classes.primaryBtn}
              variant="contained"
              disableElevation
              fullWidth
              disabled={phase === "confirming"}
              onClick={confirmPairing}
              startIcon={
                phase === "confirming" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Check size={18} />
                )
              }
            >
              {phase === "confirming"
                ? i18n.t("passkeyModal.confirming")
                : i18n.t("passkeyModal.confirmButton")}
            </Button>
          </div>
        ) : (
          <div className={classes.section}>
            {extState === "checking" && (
              <div className={classes.inlineWait}>
                <CircularProgress size={16} />
                <span>{i18n.t("passkeyModal.extChecking")}</span>
              </div>
            )}

            {extState === "present" && (
              <>
                <span className={classes.extOk}>
                  <Check size={16} /> {i18n.t("passkeyModal.extPresent")}
                </span>
                <Button
                  className={classes.primaryBtn}
                  variant="contained"
                  disableElevation
                  fullWidth
                  disabled={busy}
                  onClick={runExtensionSign}
                  startIcon={
                    busy ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {phase === "signing"
                    ? i18n.t("passkeyModal.signing")
                    : phase === "submitting"
                    ? i18n.t("passkeyModal.submitting")
                    : i18n.t("passkeyModal.signButton")}
                </Button>
                {phase === "signing" && (
                  <Typography className={classes.hint}>
                    {i18n.t("passkeyModal.signHintTab")}
                  </Typography>
                )}
              </>
            )}

            {extState === "absent" && (
              <div className={classes.installBox}>
                <Typography className={classes.sectionText}>
                  {i18n.t("passkeyModal.extAbsent")}
                </Typography>
                <div className={classes.installBtns}>
                  {firefox ? (
                    <Button
                      className={classes.installBtn}
                      variant="outlined"
                      startIcon={<ExternalLink size={16} />}
                      onClick={() => window.open(PASSKEY_FIREFOX_URL, "_blank")}
                    >
                      {i18n.t("passkeyModal.installFirefox")}
                    </Button>
                  ) : (
                    <Button
                      className={classes.installBtn}
                      variant="outlined"
                      startIcon={<ExternalLink size={16} />}
                      onClick={() =>
                        window.open(passkeyChromeStoreUrl(), "_blank")
                      }
                    >
                      {i18n.t("passkeyModal.installChrome")}
                    </Button>
                  )}
                  <Button
                    className={classes.ghostBtn}
                    startIcon={<RefreshCw size={16} />}
                    onClick={recheckExtension}
                  >
                    {i18n.t("passkeyModal.recheck")}
                  </Button>
                </div>
              </div>
            )}

            <button
              type="button"
              className={classes.fallbackLink}
              onClick={() =>
                devtoolsOpen ? setDevtoolsOpen(false) : openDevtoolsFallback()
              }
            >
              {i18n.t("passkeyModal.fallbackToggle")}
            </button>

            <Collapse in={devtoolsOpen}>
              <div className={classes.fallbackBox}>
                <Typography className={classes.sectionTitle}>
                  {i18n.t("passkeyModal.fallbackTitle")}
                </Typography>
                <ol className={classes.stepList}>
                  <li>{i18n.t("passkeyModal.fallbackStep1")}</li>
                  <li>{i18n.t("passkeyModal.fallbackStep2")}</li>
                  <li>{i18n.t("passkeyModal.fallbackStep3")}</li>
                </ol>
                <div className={classes.scriptRow}>
                  <textarea
                    className={classes.scriptArea}
                    readOnly
                    rows={4}
                    value={devtoolsScript}
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    className={classes.copyBtn}
                    size="small"
                    startIcon={
                      scriptCopied ? <Check size={16} /> : <Copy size={16} />
                    }
                    onClick={copyScript}
                  >
                    {scriptCopied
                      ? i18n.t("passkeyModal.copied")
                      : i18n.t("passkeyModal.copyScript")}
                  </Button>
                </div>
                <div className={classes.fieldLabel}>
                  {i18n.t("passkeyModal.pasteLabel")}
                </div>
                <textarea
                  className={classes.pasteArea}
                  rows={3}
                  value={pastedAssertion}
                  placeholder={i18n.t("passkeyModal.pastePlaceholder")}
                  onChange={(e) => setPastedAssertion(e.target.value)}
                />
                <Button
                  className={classes.primaryBtn}
                  variant="contained"
                  disableElevation
                  fullWidth
                  disabled={!pastedAssertion.trim() || phase === "submitting"}
                  onClick={submitPastedAssertion}
                  startIcon={
                    phase === "submitting" ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {i18n.t("passkeyModal.submitAssertion")}
                </Button>
              </div>
            </Collapse>
          </div>
        )}

        {error && <div className={classes.errorBox}>{error}</div>}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button className={classes.closeBtn} onClick={onClose}>
          {i18n.t("passkeyModal.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(PasskeyModal);
